import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Radio,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FlagIcon from '@mui/icons-material/Flag';
import * as quoteApi from './quoteApi';
import { LabourResult, Quote, QuoteLine, displayTagRef } from './quoteApi';
import VolumeFactorSlider from './VolumeFactorSlider';

interface ReviewViewProps {
  quote: Quote;
  onQuoteChange?: (quote: Quote) => void;
  onCopied?: (quote: Quote) => void;
}

const categoryLabel = (label: string) => {
  if (label === 'Fittted to DIN rail' || label === 'Fitted to DIN rail') return 'Fitted to DIN rail';
  if (label === 'Terminal Accessary' || label === 'Terminal Accessory') return 'Terminal Accessory';
  return label;
};

const PROJECT_ITEMS: Array<{ key: string; secKey: string; label: string; seconds: number }> = [
  { key: 'drill_baseplate_qty', secKey: 'drill_baseplate_sec', label: 'Drill baseplate', seconds: 3600 },
  { key: 'drill_enclosure_qty', secKey: 'drill_enclosure_sec', label: 'Drill enclosure', seconds: 3600 },
  { key: 'assemble_enclosure_qty', secKey: 'assemble_enclosure_sec', label: 'Assemble enclosure (e.g., TS8)', seconds: 360 },
  { key: 'plinth_qty', secKey: 'plinth_sec', label: 'Plinth assembly', seconds: 360 },
  { key: 'baying_qty', secKey: 'baying_sec', label: 'Baying', seconds: 600 },
  { key: 'aircon_qty', secKey: 'aircon_sec', label: 'Aircon / Heat exchanger', seconds: 0 },
  { key: 'other_qty', secKey: 'other_sec', label: 'Other', seconds: 0 },
];

const pctDisplay = (fraction?: number, fallback = 10) => {
  if (fraction == null) return fallback;
  return fraction > 1 ? fraction : Math.round(fraction * 100);
};

const TabFooter = ({ label, minutes }: { label: string; minutes: number }) => (
  <Paper variant="outlined" sx={{ mt: 1.5, px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
    <Typography variant="subtitle2" fontWeight={700}>
      {minutes.toFixed(2)} min = {(minutes / 60).toFixed(2)} hrs
    </Typography>
  </Paper>
);

const ReviewView: React.FC<ReviewViewProps> = ({ quote, onQuoteChange, onCopied }) => {
  const [tab, setTab] = useState(0);
  const [labour, setLabour] = useState<LabourResult | null>(null);
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [extraLabel, setExtraLabel] = useState('');
  const [extraQty, setExtraQty] = useState(1);
  const [extraSec, setExtraSec] = useState(5400);
  const locked = quote.status === 'finalised';
  const project = quote.project || {};
  const extras = Array.isArray(project.extra_items) ? project.extra_items as Array<{ label: string; qty: number; seconds: number }> : [];
  const volume = quote.volume_factor ?? (quote.manufactured_in_batch ? 0.8 : 1);
  const drillingMethod = Number(labour?.drilling_method || project.drilling_method || 1);

  const load = async (nextQuote?: Quote) => {
    const q = nextQuote || quote;
    const [calc, all] = await Promise.all([
      quoteApi.calculateLabour(q.id),
      quoteApi.listLines(q.id),
    ]);
    setLabour(calc);
    setLines(all.filter((l) => !l.is_deleted));
  };

  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, [quote.id]);

  const flagged = lines.filter((l) => (l.flags || []).length || (l.labour_confidence != null && l.labour_confidence < 70));

  const handleCopy = async () => {
    const copied = await quoteApi.copyQuote(quote.id);
    onCopied?.(copied);
  };

  const saveProject = async (fields: Partial<Quote> & { project_json?: Record<string, unknown> }) => {
    if (!onQuoteChange) return;
    setBusy(true);
    setError('');
    try {
      const updated = await quoteApi.updateQuote(quote.id, fields);
      onQuoteChange(updated);
      await load(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const usedKind = labour?.wiring_kind || 'Short Auto';

  const testPct = Number(project.test_pct ?? pctDisplay(labour?.test_pct, 10));
  const mmPct = Number(project.material_mgmt_pct ?? pctDisplay(labour?.material_mgmt_pct, 10));
  const hbPct = Number(project.hb_pct ?? pctDisplay(labour?.hb_pct, 14));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {labour && (
            <>
              <Chip size="small" label={`Base ${labour.base_hrs.toFixed(2)}`} />
              <Chip size="small" label={`Allowances ${labour.after_allowances_hrs.toFixed(2)}`} />
              <Chip size="small" color="primary" label={`Factored ${labour.final_hrs.toFixed(2)}`} />
              {labour.assembly_confidence != null && (
                <Chip size="small" color={labour.assembly_confidence < 70 ? 'warning' : 'success'} variant="outlined" label={`Assembly ${labour.assembly_confidence}%`} />
              )}
            </>
          )}
        </Box>
        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
          Copy as v{(quote.version || 0) + 1}
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 1, minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, px: 1.25, fontSize: 13, textTransform: 'none' } }}
      >
        <Tab label="Mechanical" />
        <Tab label="Drilling" />
        <Tab label="Electrical" />
        <Tab label="Project" />
        <Tab label="Parameters" />
      </Tabs>

      {labour && tab === 0 && (
        <Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Element</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Holes / device</TableCell>
                  <TableCell align="right">Fitting seconds</TableCell>
                  <TableCell align="right">Total time (min)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labour.mechanical_rows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{categoryLabel(row.label)}</TableCell>
                    <TableCell align="right">{row.count ?? 0}</TableCell>
                    <TableCell align="right">{row.holes_per_device || (row.count ? '—' : 0)}</TableCell>
                    <TableCell align="right">{row.seconds ?? '—'}</TableCell>
                    <TableCell align="right">{row.minutes ? row.minutes.toFixed(2) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TabFooter label="Mechanical" minutes={labour.mechanical_minutes ?? labour.mechanical_hrs * 60} />
        </Box>
      )}

      {labour && tab === 1 && (
        <Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 72 }}>Use</TableCell>
                  <TableCell>Element</TableCell>
                  <TableCell align="right">Holes</TableCell>
                  <TableCell align="right">Seconds / hole</TableCell>
                  <TableCell align="right">Total time (min)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labour.drilling_rows.map((row) => (
                  <TableRow key={row.label} selected={row.method === drillingMethod}>
                    <TableCell>
                      <Radio
                        size="small"
                        checked={row.method === drillingMethod}
                        disabled={locked || busy}
                        onChange={() => saveProject({ project_json: { ...project, drilling_method: row.method } })}
                      />
                    </TableCell>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">{typeof row.holes === 'number' ? row.holes.toFixed(row.method === 2 ? 1 : 0) : '—'}</TableCell>
                    <TableCell align="right">{row.seconds_per_hole ?? 90}</TableCell>
                    <TableCell align="right">{row.minutes.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Selected method feeds the Drilling subtotal. Method 1 is counted holes × 90s; Method 2 is 180 holes/SQM × 90s.
          </Typography>
          <TabFooter label="Drilling" minutes={labour.drilling_minutes ?? labour.drilling_hrs * 60} />
        </Box>
      )}

      {labour && tab === 2 && (
        <Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Element</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right" sx={{ fontWeight: usedKind === 'Short Auto' ? 700 : 400 }}>Short auto s/wire</TableCell>
                  <TableCell align="right" sx={{ fontWeight: usedKind === 'Auto' ? 700 : 400 }}>Auto s/wire</TableCell>
                  <TableCell align="right" sx={{ fontWeight: usedKind === 'Manual' ? 700 : 400 }}>Manual s/wire</TableCell>
                  <TableCell align="right">Total time (min)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(labour.electrical_rows || []).map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">{row.count ?? 0}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: usedKind === 'Short Auto' ? 'action.selected' : undefined }}>{row.short_auto_sec ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: usedKind === 'Auto' ? 'action.selected' : undefined }}>{row.auto_sec ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: usedKind === 'Manual' ? 'action.selected' : undefined }}>{row.manual_sec ?? '—'}</TableCell>
                    <TableCell align="right">{row.minutes ? row.minutes.toFixed(2) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Highlighted column is the rate in use ({usedKind}
            {usedKind === 'Short Auto' ? ', Cable = Auto and H+W ≤ 1500 mm' : usedKind === 'Auto' ? ', Cable = Auto and longer run' : ', Cable = Manual'}
            ). Auto and Manual apply on other projects.
          </Typography>
          <TabFooter label="Electrical" minutes={labour.electrical_minutes ?? labour.electrical_hrs * 60} />
        </Box>
      )}

      {tab === 3 && (
        <Box>
          {flagged.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {flagged.length} flagged / low-confidence lines pinned below.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Element</TableCell>
                  <TableCell align="right" sx={{ width: 140 }}>Time (seconds)</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ width: 140 }}>Total time (min)</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {PROJECT_ITEMS.map((item) => {
                  const qty = Number(project[item.key] || 0);
                  const seconds = Number(project[item.secKey] ?? item.seconds);
                  return (
                    <TableRow key={item.key}>
                      <TableCell>{item.label}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={seconds}
                          disabled={locked || busy}
                          onBlur={(e) => saveProject({ project_json: { ...project, [item.secKey]: Number(e.target.value) } })}
                          onChange={(e) => onQuoteChange?.({ ...quote, project: { ...project, [item.secKey]: Number(e.target.value) } })}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={qty}
                          disabled={locked || busy}
                          onBlur={(e) => saveProject({ project_json: { ...project, [item.key]: Number(e.target.value) } })}
                          onChange={(e) => onQuoteChange?.({ ...quote, project: { ...project, [item.key]: Number(e.target.value) } })}
                        />
                      </TableCell>
                      <TableCell align="right">{((qty * seconds) / 60).toFixed(2)}</TableCell>
                      <TableCell />
                    </TableRow>
                  );
                })}
                {extras.map((item, index) => (
                  <TableRow key={`${item.label}-${index}`}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell align="right">{item.seconds}</TableCell>
                    <TableCell align="right">{item.qty}</TableCell>
                    <TableCell align="right">{((item.qty * item.seconds) / 60).toFixed(2)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={locked}
                        onClick={() => saveProject({ project_json: { ...project, extra_items: extras.filter((_, i) => i !== index) } })}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5, mb: 1, alignItems: 'center' }}>
            <TextField size="small" placeholder="Add line" value={extraLabel} disabled={locked} onChange={(e) => setExtraLabel(e.target.value)} sx={{ width: 180 }} />
            <TextField size="small" type="number" label="Qty" value={extraQty} disabled={locked} onChange={(e) => setExtraQty(Number(e.target.value))} sx={{ width: 80 }} />
            <TextField size="small" type="number" label="Seconds" value={extraSec} disabled={locked} onChange={(e) => setExtraSec(Number(e.target.value))} sx={{ width: 110 }} />
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={locked || !extraLabel.trim()}
              onClick={() => {
                saveProject({
                  project_json: { ...project, extra_items: [...extras, { label: extraLabel.trim(), qty: extraQty, seconds: extraSec }] },
                });
                setExtraLabel('');
              }}
            >
              Add line
            </Button>
          </Box>
          {labour && <TabFooter label="Project" minutes={labour.project_minutes ?? labour.project_hrs * 60} />}
          {flagged.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tag / Ref</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Hrs</TableCell>
                    <TableCell align="right">Conf</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flagged.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>
                        <FlagIcon color="warning" sx={{ fontSize: 14, mr: 0.5 }} />
                        {displayTagRef(line) || '—'}
                        {(line.flags || []).map((flag) => (
                          <Chip key={flag} size="small" label={flag} color="warning" variant="outlined" sx={{ ml: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>{line.description}</TableCell>
                      <TableCell align="right">{line.labour_hrs?.toFixed(3)}</TableCell>
                      <TableCell align="right">{line.labour_confidence}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tab === 4 && labour && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, overflow: 'hidden' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Allowances</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 220 }}>
                <TextField
                  size="small"
                  type="number"
                  label="Test"
                  value={testPct}
                  disabled={locked || busy}
                  InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                  onBlur={(e) => saveProject({ project_json: { ...project, test_pct: Number(e.target.value) } })}
                  onChange={(e) => onQuoteChange?.({ ...quote, project: { ...project, test_pct: Number(e.target.value) } })}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Material management"
                  value={mmPct}
                  disabled={locked || busy}
                  InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                  onBlur={(e) => saveProject({ project_json: { ...project, material_mgmt_pct: Number(e.target.value) } })}
                  onChange={(e) => onQuoteChange?.({ ...quote, project: { ...project, material_mgmt_pct: Number(e.target.value) } })}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Allowance (H&B)"
                  value={hbPct}
                  disabled={locked || busy}
                  InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                  onBlur={(e) => saveProject({ project_json: { ...project, hb_pct: Number(e.target.value) } })}
                  onChange={(e) => onQuoteChange?.({ ...quote, project: { ...project, hb_pct: Number(e.target.value) } })}
                />
              </Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 0.5 }}>
                Batch / volume factor · {Math.round(volume * 100)}%
              </Typography>
              <VolumeFactorSlider
                value={volume}
                disabled={locked || busy}
                onChangeCommitted={(_, value) => saveProject({ volume_factor: Number(value) })}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Rollup (Mechanical + Electrical + Drilling + Project)
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Sub Total 1 (BASE)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{labour.base_hrs.toFixed(2)} hrs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Test ({testPct}%)</TableCell>
                    <TableCell align="right">{labour.test_hrs.toFixed(2)} hrs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Material management ({mmPct}%)</TableCell>
                    <TableCell align="right">{labour.material_mgmt_hrs.toFixed(2)} hrs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>+ Allowance H&B ({hbPct}%)</TableCell>
                    <TableCell align="right">{labour.hb_hrs.toFixed(2)} hrs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Sub Total 2</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{labour.after_allowances_hrs.toFixed(2)} hrs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>× Batch factor ({Math.round((labour.volume_factor || volume) * 100)}%)</TableCell>
                    <TableCell align="right">
                      {(labour.final_hrs - labour.after_allowances_hrs).toFixed(2)} hrs
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{labour.final_hrs.toFixed(2)} hrs</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Box>
          <Paper variant="outlined" sx={{ mt: 1.5, px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={700}>Final labour total (all tabs, after allowances and batch factor)</Typography>
            <Typography variant="subtitle2" fontWeight={700}>{labour.final_hrs.toFixed(2)} hrs</Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ReviewView;
